import { ContentAttachments } from '@/components/widget/content-attachments';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import { getContent } from '@/api/content-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateCalendarEventFromEventContext,
  useMemberExternalAccounts,
  useNavigateWithContentFlush,
  useUpdateCalendarEventFromEventContext,
} from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { CANONICAL_LABELS } from '@/lib/content-labels';
import { toContentHref } from '@/utils/content-ref';
import { Event } from '@vivport/backend/types';
import { useQueries } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Notice } from '@/components/ui/notice';

interface DetailsEventProps {
  content: Event;
  onDescriptionSave: (description: string) => Promise<void>;
  isRegenerating: boolean;
  memberId: number;
}

type ParsedProcessingDescription = {
  stage: string;
  processingAnchorId?: number;
  createdAnchorIds: number[];
  sourceFileIds: number[];
  extractedJsonFileIds: number[];
  createdEventIds: number[];
  filesCompleted: number;
  filesTotal: number;
  totals?: {
    parsedRows: number;
    validRows: number;
    created: number;
    conflicts: number;
    errors: number;
  };
  status?: 'SUCCESS' | 'PARTIAL' | 'FAILURE';
  failure?: string;
};

type ProcessingDescriptionParseResult =
  | { parsed: ParsedProcessingDescription; parseError: null }
  | { parsed: null; parseError: string };

function formatPartyRoleLabel(role: Event['parties'][number]['role']): string {
  return role.role
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseIdList(value: string): number[] {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '(none)') {
    return [];
  }
  return trimmed
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
}

function parseProcessingDescription(description: string): ProcessingDescriptionParseResult | null {
  const lines = description
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const stageLine = lines.find((line) => line.startsWith('Event import processing stage:'));
  if (!stageLine) {
    return null;
  }

  const stage = stageLine.replace('Event import processing stage:', '').trim();
  const sourceFileLine = lines.find((line) => line.startsWith('Source files:'));
  const extractedJsonLine = lines.find((line) => line.startsWith('Final normalized JSON files:'));
  const processingAnchorLine = lines.find((line) => line.startsWith('Processing anchor ID:'));
  const createdAnchorsLine = lines.find((line) => line.startsWith('Created anchor IDs:'));
  const createdEventsLine = lines.find((line) => line.startsWith('Created events:'));
  const progressLine = lines.find((line) => line.startsWith('Progress:'));
  const totalsLine = lines.find((line) => line.startsWith('Totals:'));
  const finalStatusLine = lines.find((line) => line.startsWith('Final status:'));
  const failureLine = lines.find((line) => line.startsWith('Failure:'));

  const progressMatch = progressLine?.match(/Progress:\s*(\d+)\/(\d+)\s*file\(s\)\s*completed/i);
  if (!progressMatch) {
    return {
      parsed: null,
      parseError: 'Import processing details are malformed (missing valid progress line).',
    };
  }

  let totals: ParsedProcessingDescription['totals'] | undefined;
  if (totalsLine) {
    const totalsMatch = totalsLine.match(
      /Totals:\s*parsed=(\d+),\s*valid=(\d+),\s*created=(\d+),\s*conflicts=(\d+),\s*errors=(\d+)/i
    );
    if (!totalsMatch) {
      return {
        parsed: null,
        parseError: 'Import processing details are malformed (invalid totals line).',
      };
    }
    totals = {
      parsedRows: Number(totalsMatch[1]),
      validRows: Number(totalsMatch[2]),
      created: Number(totalsMatch[3]),
      conflicts: Number(totalsMatch[4]),
      errors: Number(totalsMatch[5]),
    };
  }

  const statusRaw = finalStatusLine?.replace('Final status:', '').trim();
  const status =
    statusRaw === 'SUCCESS' || statusRaw === 'PARTIAL' || statusRaw === 'FAILURE' ? statusRaw : undefined;

  return {
    parsed: {
      stage,
      processingAnchorId: (() => {
        const raw = processingAnchorLine?.replace('Processing anchor ID:', '').trim();
        const parsed = raw ? Number(raw) : NaN;
        return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
      })(),
      createdAnchorIds: parseIdList(createdAnchorsLine?.replace('Created anchor IDs:', '') ?? ''),
      sourceFileIds: parseIdList(sourceFileLine?.replace('Source files:', '') ?? ''),
      extractedJsonFileIds: parseIdList(extractedJsonLine?.replace('Final normalized JSON files:', '') ?? ''),
      createdEventIds: parseIdList(createdEventsLine?.replace('Created events:', '') ?? ''),
      filesCompleted: Number(progressMatch[1]),
      filesTotal: Number(progressMatch[2]),
      totals,
      status,
      failure: ((value) => (value && value !== '(none)' ? value : undefined))(
        failureLine?.replace('Failure:', '').trim()
      ),
    },
    parseError: null,
  };
}

function ContentIdLinks({ ids }: { ids: number[] }) {
  const navigate = useNavigateWithContentFlush();
  const contentQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['content-link-ref', id],
      queryFn: () => getContent(id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const refsById = new Map<number, string>();
  ids.forEach((id, index) => {
    const code = contentQueries[index]?.data?.code;
    refsById.set(id, code ?? String(id));
  });

  if (ids.length === 0) {
    return <span className="text-muted-foreground">(none)</span>;
  }
  return (
    <span className="flex flex-wrap gap-2">
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          className="text-primary underline-offset-2 hover:underline"
          onClick={() => navigate(toContentHref(refsById.get(id) ?? String(id)))}
        >
          {refsById.get(id) ?? id}
        </button>
      ))}
    </span>
  );
}

export function DetailsEvent({
  content: event,
  onDescriptionSave,
  isRegenerating,
  memberId,
}: DetailsEventProps) {
  const accountsQuery = useMemberExternalAccounts(memberId);
  const createMutation = useCreateCalendarEventFromEventContext();
  const updateMutation = useUpdateCalendarEventFromEventContext();
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [providerCalendarId, setProviderCalendarId] = useState('');

  const linkedExternalProvider =
    event.provider === 'GOOGLE_CALENDAR' ? 'GOOGLE' : event.provider === 'MICROSOFT_CALENDAR' ? 'MICROSOFT' : null;
  const providerLabel = (p: 'GOOGLE' | 'MICROSOFT'): string => (p === 'MICROSOFT' ? 'Microsoft' : 'Google');

  const eligibleCalendarAccounts = useMemo(
    () =>
      (accountsQuery.data?.items ?? []).filter(
        (item) =>
          item.calendarWriteCapable &&
          item.isActive &&
          (linkedExternalProvider ? item.provider === linkedExternalProvider : true)
      ),
    [accountsQuery.data?.items, linkedExternalProvider]
  );

  useEffect(() => {
    if (eligibleCalendarAccounts.length === 0) {
      if (selectedAccountId) {
        setSelectedAccountId('');
      }
      return;
    }

    if (
      !selectedAccountId ||
      !eligibleCalendarAccounts.some((account) => String(account.id) === selectedAccountId)
    ) {
      setSelectedAccountId(String(eligibleCalendarAccounts[0].id));
    }
  }, [eligibleCalendarAccounts, selectedAccountId]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const linkedCalendarEvent = linkedExternalProvider !== null && Boolean(event.providerEventId && event.providerCalendarId);

  const panelProvider =
    linkedExternalProvider ??
    eligibleCalendarAccounts.find((a) => String(a.id) === selectedAccountId)?.provider ??
    null;
  const panelProviderLabel = panelProvider ? providerLabel(panelProvider) : null;

  const resolveSelectedEligibleAccountId = (): number | null => {
    const parsed = Number(selectedAccountId);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }
    return eligibleCalendarAccounts.some((account) => account.id === parsed) ? parsed : null;
  };

  const handleCreateCalendarEvent = async (): Promise<void> => {
    const accountId = resolveSelectedEligibleAccountId();
    if (!accountId) {
      toast.error('Choose an account with calendar write access.');
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        sourceContentId: event.id,
        input: {
          memberExternalAccountId: accountId,
          ...(providerCalendarId.trim().length > 0 ? { providerCalendarId: providerCalendarId.trim() } : {}),
        },
      });
      toast.success(
        result.created
          ? 'Calendar event created from this event.'
          : 'Event is already linked to a calendar.'
      );
    } catch (error) {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to create calendar event.'));
    }
  };

  const handleUpdateCalendarEvent = async (): Promise<void> => {
    const accountId = resolveSelectedEligibleAccountId();
    if (!accountId) {
      toast.error('Choose an account with calendar write access.');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        sourceContentId: event.id,
        input: {
          memberExternalAccountId: accountId,
          ...(providerCalendarId.trim().length > 0 ? { providerCalendarId: providerCalendarId.trim() } : {}),
        },
      });
      toast.success('Calendar event updated from this event.');
    } catch (error) {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to update calendar event.'));
    }
  };

  const parsedImportResult = parseProcessingDescription(event.description ?? '');
  const parsedImport = parsedImportResult?.parsed ?? null;
  const parseError = parsedImportResult?.parseError ?? null;

  return (
    <div data-testid="details-event" className="space-y-4">
      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Description</h3>
        <ContentDescriptionEditable
          description={event.description || ''}
          onSave={onDescriptionSave}
          disabled={isRegenerating}
          placeholder={isRegenerating ? "Regeneration currently in progress" : "Click to add description"}
        />
      </div>

      {parsedImport ? (
        <div className="rounded-md border bg-muted/20 p-3 space-y-2">
          <h3 className="font-medium">Import processing details</h3>
          <div className="grid gap-1 text-sm">
            <p>
              <span className="font-medium">Stage:</span> {parsedImport.stage}
            </p>
            <p>
              <span className="font-medium">Progress:</span> {parsedImport.filesCompleted}/{parsedImport.filesTotal}{' '}
              file(s) completed
            </p>
            {parsedImport.status ? (
              <p>
                <span className="font-medium">Status:</span> {parsedImport.status}
              </p>
            ) : null}
            {parsedImport.totals ? (
              <p>
                <span className="font-medium">Totals:</span> parsed {parsedImport.totals.parsedRows}, valid{' '}
                {parsedImport.totals.validRows}, created {parsedImport.totals.created}, conflicts{' '}
                {parsedImport.totals.conflicts}, errors {parsedImport.totals.errors}
              </p>
            ) : null}
            {parsedImport.failure ? (
              <p className="text-destructive">
                <span className="font-medium">Failure:</span> {parsedImport.failure}
              </p>
            ) : null}
            <p>
              <span className="font-medium">Source files:</span> <ContentIdLinks ids={parsedImport.sourceFileIds} />
            </p>
            <p>
              <span className="font-medium">Processing anchor:</span>{' '}
              <ContentIdLinks ids={parsedImport.processingAnchorId ? [parsedImport.processingAnchorId] : []} />
            </p>
            <p>
              <span className="font-medium">Created anchors:</span> <ContentIdLinks ids={parsedImport.createdAnchorIds} />
            </p>
            <p>
              <span className="font-medium">Final normalized JSON files:</span>{' '}
              <ContentIdLinks ids={parsedImport.extractedJsonFileIds} />
            </p>
            <p>
              <span className="font-medium">Created events:</span> <ContentIdLinks ids={parsedImport.createdEventIds} />
            </p>
          </div>
        </div>
      ) : null}
      {!parsedImport && parseError ? (
        <Notice tone="destructive" className="p-3">
          <p className="text-sm text-destructive">
            {parseError} Raw import description is shown in the Description field above.
          </p>
        </Notice>
      ) : null}

      <div className="rounded-md border bg-card p-4 space-y-3">
        <div>
          <h3 className="font-medium">Parties</h3>
          {event.parties.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">No parties extracted.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {event.parties.map((party) => (
                <li key={`${party.partyId}-${party.role.kind}-${party.role.role}`}>
                  <span className="font-medium">{formatPartyRoleLabel(party.role)}:</span>{' '}
                  {party.name ? `${party.name} ` : ''}
                  ({party.email})
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-md border bg-card p-4 space-y-3">
        <div>
          <h3 className="font-medium">{panelProviderLabel ? `${panelProviderLabel} Calendar sync` : 'Calendar sync'}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {panelProviderLabel
              ? `Write this canonical event to ${panelProviderLabel} Calendar.`
              : 'Write this canonical event to the selected calendar.'}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="event-calendar-account">Calendar account</Label>
            {eligibleCalendarAccounts.length > 1 ? (
              <Select
                value={selectedAccountId}
                onValueChange={setSelectedAccountId}
                disabled={accountsQuery.isLoading || isSubmitting}
              >
                <SelectTrigger id="event-calendar-account">
                  <SelectValue placeholder="Choose account" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleCalendarAccounts.map((account) => (
                    <SelectItem key={account.id} value={String(account.id)}>
                      {account.externalEmail}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="event-calendar-account"
                value={eligibleCalendarAccounts[0]?.externalEmail ?? ''}
                placeholder="No eligible account"
                disabled
              />
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="event-google-calendar-id">Calendar ID (optional)</Label>
            <Input
              id="event-calendar-id"
              value={providerCalendarId}
              onChange={(eventInput) => setProviderCalendarId(eventInput.target.value)}
              placeholder="primary"
              disabled={isSubmitting || eligibleCalendarAccounts.length === 0}
            />
          </div>
        </div>

        {!accountsQuery.isLoading && eligibleCalendarAccounts.length === 0 ? (
          <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            Connect an active {linkedExternalProvider ? providerLabel(linkedExternalProvider) : 'Google or Microsoft'} account with calendar write access to sync this event.
          </div>
        ) : null}

        <div className="grid gap-1 text-sm text-muted-foreground">
          <p>Linked provider: {event.provider ?? 'VIVPORT'}</p>
          <p>Linked calendar id: {event.providerCalendarId ?? '-'}</p>
          <p>Linked event id: {event.providerEventId ?? '-'}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={handleCreateCalendarEvent}
            disabled={isSubmitting || eligibleCalendarAccounts.length === 0 || linkedCalendarEvent}
          >
            {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create in calendar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleUpdateCalendarEvent}
            disabled={isSubmitting || eligibleCalendarAccounts.length === 0 || !linkedCalendarEvent}
          >
            {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Update in calendar
          </Button>
        </div>
      </div>

      <ContentAttachments
        parentContentId={event.id}
        title="Attachments"
        emptyText="No attachments yet"
        breadcrumbLabel={CANONICAL_LABELS.event}
      />
    </div>
  );
}
